import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Receipt, ArrowRight, Lightning, Wallet, ArrowUp, ArrowDown, Repeat, CreditCard, Warning, Signature } from "@phosphor-icons/react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useCurrency } from "../../lib/useCurrency";
import NotificationToggle from "../../components/NotificationToggle";

function nextBillDate() {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
}

function daysUntil(date) {
  const ms = date.getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
}

export default function ClientDashboard() {
  const { user } = useAuth();
  const { symbol } = useCurrency();
  const [sessions, setSessions] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [credits, setCredits] = useState({ balance: 0, transactions: [] });
  const [plan, setPlan] = useState(user?.monthly_plan || null);
  const [sessionPlan, setSessionPlan] = useState(null);
  const [hasDirectDebit, setHasDirectDebit] = useState(true);
  const [formsComplete, setFormsComplete] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, i, c, me, pm, sp] = await Promise.all([
          api.get("/sessions", { params: { from_date: new Date().toISOString() } }),
          api.get("/invoices", { params: { status: "unpaid" } }),
          api.get("/me/credits"),
          api.get("/auth/me"),
          api.get("/clients/me/payment-method").catch(() => ({ data: { has_method: false } })),
          api.get("/clients/me/session-plan").catch(() => ({ data: { on_plan: false } })),
        ]);
        setSessions(s.data.slice(0, 5));
        setInvoices(i.data);
        setCredits(c.data);
        setPlan(me.data?.monthly_plan || null);
        setHasDirectDebit(!!pm.data?.has_method);
        setSessionPlan(sp.data?.on_plan ? sp.data : null);
      } catch (_e) {}
      try {
        const { data: f } = await api.get("/forms/onboarding");
        setFormsComplete(!!f.complete);
      } catch (_e) {}
    })();
  }, []);

  const unpaidTotal = invoices.reduce((s, i) => s + i.amount, 0);
  const next = sessions[0];
  const balance = Number(credits.balance || 0);
  const lowBalance = balance < 100;
  const recentTxns = credits.transactions.slice(0, 5);
  const nextBill = nextBillDate();
  const daysToBill = daysUntil(nextBill);
  const planActive = plan && plan.active && Number(plan.amount || 0) > 0;

  return (
    <div className="space-y-10" data-testid="client-dashboard">
      <div>
        <div className="tracker text-brand mb-3">[ Welcome back ]</div>
        <h1 className="font-display text-5xl sm:text-6xl leading-[0.9]">{(user?.name || "").split(" ")[0]}.</h1>
        <p className="text-zinc-400 mt-3">Here&apos;s where you stand.</p>
      </div>

      {!hasDirectDebit && (
        <Link
          to="/client/payment-method"
          className="card-flat p-5 flex items-center gap-4 border-amber-500/40 bg-amber-500/5 hover:bg-amber-500/10 transition-colors"
          data-testid="dd-required-banner"
        >
          <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
            <Warning size={20} weight="duotone" className="text-amber-400" />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-amber-300">Set up direct debit to start booking</div>
            <div className="text-xs text-zinc-400 mt-1">Add a card so your monthly payment is taken automatically — no chasing.</div>
          </div>
          <div className="flex items-center gap-2 text-amber-300 tracker">
            <CreditCard size={14} weight="bold" /> SET UP <ArrowRight size={12} weight="bold" />
          </div>
        </Link>
      )}

      {!formsComplete && (
        <Link
          to="/client/forms"
          className="card-flat p-5 flex items-center gap-4 border-brand/40 bg-brand/5 hover:bg-brand/10 transition-colors"
          data-testid="forms-required-banner"
        >
          <div className="w-10 h-10 rounded-full bg-brand/20 flex items-center justify-center flex-shrink-0">
            <Signature size={20} weight="duotone" className="text-brand-light" />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-brand-lighter">Complete your health forms</div>
            <div className="text-xs text-zinc-400 mt-1">Fill in the PAR-Q questionnaire and sign the waiver — takes 3 minutes.</div>
          </div>
          <div className="flex items-center gap-2 text-brand-lighter tracker">
            COMPLETE <ArrowRight size={12} weight="bold" />
          </div>
        </Link>
      )}

      <NotificationToggle />

      {/* Top: Sessions/Pot + Next session */}
      <div className="grid lg:grid-cols-12 gap-6">
        {sessionPlan ? (
          <SessionsRemainingCard sp={sessionPlan} symbol={symbol} />
        ) : (
          <div className="lg:col-span-5 card-flat p-8 relative overflow-hidden" data-testid="pot-card">
            <div className="flex items-center gap-2 tracker text-brand mb-4">
              <Wallet size={14} weight="duotone" /> [ Your pot ]
            </div>
            <div className={`font-display text-6xl mb-2 money ${balance < 0 ? "text-[#FF6B61]" : ""}`} data-testid="pot-balance">
              {symbol}{balance.toFixed(2)}
            </div>
            <div className="tracker mb-6">
              {balance < 0
                ? "Owed — top up to keep training"
                : lowBalance
                ? "Running low — top up to keep training"
                : "Pre-paid balance · debits on booking"}
            </div>
            {(invoices.length > 0 || lowBalance) && (
              <Link to="/client/invoices" className="btn-primary" data-testid="topup-cta">
                <Receipt size={16} weight="duotone" />
                {invoices.length > 0 ? <>Top up — <span className="money">{symbol}{unpaidTotal.toFixed(2)}</span> pending</> : "Top up"}
              </Link>
            )}
            <Wallet size={140} weight="duotone" className="absolute -right-8 -bottom-8 text-brand/10" />
          </div>
        )}

        <div className="lg:col-span-7 card-flat p-8 relative overflow-hidden">
          <div className="tracker text-brand mb-4">[ Next session ]</div>
          {next ? (
            <>
              <div className="font-display text-4xl sm:text-5xl mb-3">{next.session_type}</div>
              <div className="text-zinc-400 mb-6">
                {new Date(next.start_time).toLocaleString(undefined, { weekday: "long", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" })} · {next.duration_minutes} min
              </div>
              <Link to="/client/sessions" className="btn-ghost" data-testid="view-sessions-cta">
                View all sessions <ArrowRight size={14} weight="bold" />
              </Link>
            </>
          ) : (
            <>
              <div className="font-display text-3xl mb-3">No upcoming sessions.</div>
              <div className="text-zinc-400 mb-6">Your coach hasn't scheduled anything yet.</div>
            </>
          )}
          <Lightning size={140} weight="duotone" className="absolute -right-8 -bottom-8 text-brand/10" />
        </div>
      </div>

      {/* Monthly plan banner */}
      {planActive && (
        <div className="card-flat p-6 relative overflow-hidden grid md:grid-cols-12 gap-6 items-center" data-testid="monthly-plan-card">
          <div className="md:col-span-5 flex items-center gap-4">
            <div className="w-12 h-12 border border-brand/40 bg-brand/10 flex items-center justify-center">
              <Repeat size={22} weight="duotone" className="text-brand" />
            </div>
            <div>
              <div className="tracker text-brand mb-1">[ Monthly plan ]</div>
              <div className="font-display text-2xl leading-none">{plan.description || "Monthly package"}</div>
            </div>
          </div>
          <div className="md:col-span-3" data-testid="plan-amount-cell">
            <div className="tracker mb-1">Amount</div>
            <div className="font-display text-3xl money">{symbol}{Number(plan.amount).toFixed(2)}<span className="text-zinc-500 text-base font-mono"> /mo</span></div>
          </div>
          <div className="md:col-span-4" data-testid="next-bill-cell">
            <div className="tracker mb-1">Next bill</div>
            <div className="font-display text-2xl">
              {nextBill.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}
            </div>
            <div className="tracker text-zinc-500 mt-1">in {daysToBill} {daysToBill === 1 ? "day" : "days"}</div>
          </div>
        </div>
      )}

      {/* Bottom: Upcoming + Recent ledger */}
      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 card-flat">
          <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
            <div className="font-display text-2xl">Upcoming</div>
            <Calendar size={18} weight="duotone" className="text-brand" />
          </div>
          {sessions.length === 0 ? (
            <div className="p-10 text-center text-zinc-500 tracker">Nothing scheduled</div>
          ) : (
            sessions.map((s) => {
              const dt = new Date(s.start_time);
              return (
                <div key={s.id} className="px-6 py-4 border-b border-white/5 last:border-b-0 flex items-center gap-4">
                  <div className="w-14 text-center">
                    <div className="font-display text-2xl text-brand leading-none">{dt.getDate()}</div>
                    <div className="tracker mt-1">{dt.toLocaleDateString(undefined, { month: "short" })}</div>
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold">{s.session_type}</div>
                    <div className="text-xs text-zinc-500">{s.duration_minutes} min · {s.status}</div>
                  </div>
                  <div className="font-mono text-sm">{dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                </div>
              );
            })
          )}
        </div>

        <div className="lg:col-span-5 card-flat" data-testid="ledger-widget">
          <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
            <div className="font-display text-2xl">Pot ledger</div>
            <Wallet size={18} weight="duotone" className="text-brand" />
          </div>
          {recentTxns.length === 0 ? (
            <div className="p-10 text-center text-zinc-500 tracker">No activity yet</div>
          ) : (
            recentTxns.map((t) => (
              <div key={t.id} className="px-6 py-3 border-b border-white/5 last:border-b-0 flex items-center gap-3">
                {t.amount >= 0 ? (
                  <ArrowUp size={16} weight="bold" className="text-emerald-400" />
                ) : (
                  <ArrowDown size={16} weight="bold" className="text-[#FF6B61]" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{t.description}</div>
                  <div className="tracker">{new Date(t.created_at).toLocaleDateString()}</div>
                </div>
                <div className={`font-mono text-sm font-semibold money ${t.amount >= 0 ? "text-emerald-400" : "text-[#FF6B61]"}`}>
                  {t.amount >= 0 ? "+" : "-"}{symbol}{Math.abs(t.amount).toFixed(2)}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function SessionsRemainingCard({ sp, symbol }) {
  const remaining = Number(sp.sessions_remaining || 0);
  const monthlyCount = Number(sp.monthly_session_count || 0);
  const pendingCredit = Number(sp.session_credits_pending || 0);
  const perSession = Number(sp.per_session_rate || 0);
  // Next reset = 1st of next month
  const now = new Date();
  const nextReset = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const resetLabel = nextReset.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
  const ratio = monthlyCount > 0 ? Math.max(0, Math.min(1, remaining / monthlyCount)) : 0;
  const low = remaining <= 1;
  return (
    <div className="lg:col-span-5 card-flat p-8 relative overflow-hidden" data-testid="sessions-remaining-card">
      <div className="flex items-center gap-2 tracker text-brand mb-4">
        <Lightning size={14} weight="duotone" /> [ Sessions remaining ]
      </div>
      <div className={`font-display text-6xl mb-1 ${low ? "text-amber-300" : ""}`} data-testid="sessions-remaining-count">
        {remaining}
        <span className="text-2xl text-zinc-500 ml-2">/ {monthlyCount}</span>
      </div>
      <div className="tracker mb-4">
        {remaining === 0
          ? "All used — ask for an ad-hoc to keep going"
          : low
          ? "Last one this month"
          : `Resets ${resetLabel}`}
      </div>
      {/* Progress bar */}
      <div className="w-full h-1 bg-white/10 mb-6 relative overflow-hidden">
        <div
          className={`absolute inset-y-0 left-0 ${low ? "bg-amber-400" : "bg-brand"}`}
          style={{ width: `${ratio * 100}%` }}
        />
      </div>
      <div className="space-y-2 text-xs text-zinc-500 mb-4">
        <div className="flex justify-between">
          <span>Per-session value</span>
          <span className="font-mono text-zinc-300">{symbol}{perSession.toFixed(2)}</span>
        </div>
        {pendingCredit > 0 && (
          <div className="flex justify-between">
            <span>Credit on next bill</span>
            <span className="font-mono text-emerald-400" data-testid="pending-credit">-{symbol}{pendingCredit.toFixed(2)}</span>
          </div>
        )}
      </div>
      <Lightning size={140} weight="duotone" className="absolute -right-8 -bottom-8 text-brand/10" />
    </div>
  );
}

